import React, { FC, useEffect, useRef } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { htmlToMarkdown, markdownToHtml } from './handbookMarkdown';

interface HandbookRichTextEditorProps {
  /** The page body as Markdown; the editor shows it and reports edits back as Markdown. */
  value: string;
  disabled: boolean;
  placeholder: string;
  labelledBy: string;
  onChange: (bodyMarkdown: string) => void;
}

interface ToolbarState {
  heading: boolean;
  bold: boolean;
  italic: boolean;
  bulletList: boolean;
  orderedList: boolean;
  link: boolean;
}

const readToolbarState = (editor: Editor): ToolbarState => ({
  heading: editor.isActive('heading'),
  bold: editor.isActive('bold'),
  italic: editor.isActive('italic'),
  bulletList: editor.isActive('bulletList'),
  orderedList: editor.isActive('orderedList'),
  link: editor.isActive('link'),
});

interface ToolButtonProps {
  label: string;
  title: string;
  active?: boolean;
  disabled: boolean;
  className?: string;
  onClick: () => void;
}

const ToolButton: FC<ToolButtonProps> = ({
  label,
  title,
  active = false,
  disabled,
  className,
  onClick,
}) => (
  <button
    type='button'
    className={`handbook-tool${active ? ' active' : ''}${className ? ` ${className}` : ''}`}
    title={title}
    aria-label={title}
    aria-pressed={active}
    disabled={disabled}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
  >
    {label}
  </button>
);

/**
 * WYSIWYG surface for a handbook page body. The editor holds HTML; every
 * change is serialised back to Markdown through `handbookMarkdown`, so the
 * value the caller sees (and saves) is always Markdown.
 */
export const HandbookRichTextEditor: FC<HandbookRichTextEditorProps> = ({
  value,
  disabled,
  placeholder,
  labelledBy,
  onChange,
}) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmitted = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        underline: false,
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
        },
      }),
      TableKit.configure({ table: { resizable: false } }),
      Placeholder.configure({ placeholder }),
    ],
    content: markdownToHtml(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'handbook-preview handbook-rich-text-content',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelledBy,
      },
    },
    onUpdate: ({ editor: instance }) => {
      const markdown = htmlToMarkdown(instance.getHTML());
      lastEmitted.current = markdown;
      onChangeRef.current(markdown);
    },
  });

  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    lastEmitted.current = value;
    editor.commands.setContent(markdownToHtml(value), { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    if (editor && editor.isEditable === disabled) editor.setEditable(!disabled);
  }, [editor, disabled]);

  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) =>
      instance ? readToolbarState(instance) : null,
  });

  if (!editor || !state) return null;

  const chain = () => editor.chain().focus();

  const editLink = () => {
    const previous = editor.getAttributes('link').href;
    const href = window.prompt(
      'Link address',
      typeof previous === 'string' ? previous : 'https://',
    );
    if (href === null) return;
    const trimmed = href.trim();
    if (!trimmed || trimmed === 'https://') {
      chain().extendMarkRange('link').unsetLink().run();
      return;
    }
    chain().extendMarkRange('link').setLink({ href: trimmed }).run();
  };

  return (
    <div
      className={`handbook-rich-text${disabled ? ' disabled' : ''}`}
      data-testid='handbook-rich-text'
    >
      <div
        className='handbook-rich-text-toolbar'
        role='toolbar'
        aria-label='Formatting'
      >
        <ToolButton
          label='Heading'
          title='Heading'
          active={state.heading}
          disabled={disabled}
          onClick={() => chain().toggleHeading({ level: 2 }).run()}
        />
        <span className='handbook-tool-divider' aria-hidden='true' />
        <ToolButton
          label='B'
          title='Bold'
          className='bold'
          active={state.bold}
          disabled={disabled}
          onClick={() => chain().toggleBold().run()}
        />
        <ToolButton
          label='I'
          title='Italic'
          className='italic'
          active={state.italic}
          disabled={disabled}
          onClick={() => chain().toggleItalic().run()}
        />
        <span className='handbook-tool-divider' aria-hidden='true' />
        <ToolButton
          label='• List'
          title='Bullet list'
          active={state.bulletList}
          disabled={disabled}
          onClick={() => chain().toggleBulletList().run()}
        />
        <ToolButton
          label='1. List'
          title='Numbered list'
          active={state.orderedList}
          disabled={disabled}
          onClick={() => chain().toggleOrderedList().run()}
        />
        <ToolButton
          label='Link'
          title={state.link ? 'Edit link' : 'Add link'}
          active={state.link}
          disabled={disabled}
          onClick={editLink}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default HandbookRichTextEditor;
